import { defineStore, acceptHMRUpdate } from 'pinia'

import { apiClient, apiClientWithoutAuth, apiStrapi } from "@/helpers/api.js"
import { useGlobalStore } from '@/stores/GlobalStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { handleError, handleApiError } from '@/services/errorHandler'
import { CHANNELS_BY_TYPE, JOB_PROGRESS_MESSAGES } from '@/constants'

export const useUserStore = defineStore('UserStore', {
  state: () => {
    return {
      user: null,
      loading: true,
      loggedIn: false,

      settings: {},
      connectionCredentials: [],
      // email: '',
      token: '',
      overrideAuth: false,
      loginError: null,
      
      plan: 'Rookie',

      subscriptions: [],
      titleTemplates: [],
      descriptionTemplates: [],
      selectedTemplate: {
        name: '',
        content: '',
        default: false,
        id: null
      },
      customFields: [],
      uploadingAvatar: false,
      uploadingBanner: false,
      loadingTemplates: false,
      verificationFail: false,
      verificationLinkExpired: false,
      verificationSuccess: false,
      updatePasswordFail: false,
      updatePasswordSuccess: false,
      emailAlreadyExists: false,
      userCreated: false,
      notFoundEmail: false,

      scanners: [],

      lookupEmailInfo: null,

      promoCodeInfo: {},
      promoCodeError: null,
      promoCodeApplied: false,

      paymentMethod: {},

      changePasswordError: null,
      lookupWinbackInfo: null,

      linkToMarketplaceLoader: false,
      redirectMobileScanUrl: '',
      loginOtpPasswordLoader: false,
      loginOtpPasswordErrorMessage: '',
      otpLoginDone: false,

      tax: {},
      selectedPlanPrice: 0,
      validAddress: true,

      loadingSettings: false,
      loadingConnectionCredentials: false,
    }
  },
  getters: {
    flags() {
      if (!this.user || !this.user.flags) return {} 
      return JSON.parse(this.user.flags)
    },
    importingProgress() {
      if (!this.user) return null
      typeof this.user.import_progress === 'string' && (this.user.import_progress = JSON.parse(this.user.import_progress))
      return this.user.import_progress
    },
    getEntitlements() {
      return this.user?.entitlements || []
    },

    // Use for flag-only checks (enabled, token_set, refresh_token_set).
    // For raw token access call fetchConnectionCredentialByType() directly — don't cache sensitive fields in state.
    credentialByType: (state) => (type) => {
      return state.connectionCredentials?.find(c => c.type === type) ?? null
    },

    hasAnyMarketplaceEnabled() {
      return ['collx', 'ebay', 'whatnot', 'shopify'].some(
        (key) => this.credentialByType(key)?.enabled || this.user?.[`${key}_enabled`]
      )
    },

    // ── Internal rollout gate: `inventory-updates` ──────────────────────────
    // The whole Inventory redesign ships behind this user flag while it's being
    // tested internally. Defined HERE rather than in `helpers/featureFlags.js`
    // because `isNewDesign` below depends on it, and that file imports this
    // store — reading it from there would be a circular import. The composable
    // `useInventoryUpdates()` just re-exposes this getter.
    // Localhost is always on, matching every other flag in the app.
    hasInventoryUpdates() {
      if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') return true

      const flags = this.flags

      return !!(flags?.['inventory-updates'] || flags?.inventory_updates)
    },

    // ── New-design opt-in flag ──────────────────────────────────────────────
    // Single source of truth for the "new design" rollout. Everything gated
    // behind the new design (new views, toolbars, actions) must check this —
    // via `userStore.isNewDesign` or the `useNewDesign()` composable — so the
    // whole feature can be flipped from one place (`user.data.about.new_design`).
    //
    // AND-ed with the rollout flag: a user who somehow has `new_design: true`
    // without `inventory-updates` still gets the classic app. That's the whole
    // point of the gate — one check, and every downstream consumer (grids,
    // cells, inspector, toolbars) follows without its own flag test.
    isNewDesign() {
      return this.hasInventoryUpdates && this.user?.data?.about?.new_design === true
    },

    // True once the user has dismissed the FIRST "try the new view" welcome
    // prompt ("Maybe later"). Stored next to `new_design` in `data.about` so the
    // flags travel together. Triggers the stage-2 follow-up prompt.
    newDesignPromptDismissed() {
      return this.user?.data?.about?.new_design_dismissed === true
    },

    // True once the user has dismissed the SECOND (follow-up) prompt. This is
    // the final dismissal — no welcome modal is ever shown again after it.
    newDesignPromptDismissedFinal() {
      return this.user?.data?.about?.new_design_dismissed_final === true
    },

    // True once the first-run new-design guided tour has been completed/skipped.
    newDesignTourSeen() {
      return this.user?.data?.about?.new_design_tour_seen === true
    },

  },
  actions: {
    updateUserField(field, value) {
      const configStore = useConfigStore()

      configStore.ignoreConfigUpdate = true

      this.user = { ...this.user, [field]: value }

      const authData = localStorage.getItem("cdp_auth")
      const sessionData = JSON.parse(authData)

      // possible if we get auth from nova login
      if (!sessionData) {
        localStorage.setItem("cdp_auth", JSON.stringify({ user: this.user }))
        return
      }

      sessionData.user = { ...sessionData.user, ...this.user }
      localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
    },

    async checkSession(overrideAuthToken = null) {
      if (overrideAuthToken) {
        try {
          this.token = overrideAuthToken
          const response = await apiClient().get(`/users/me`)
          this.user = response.data
          this.loggedIn = true  
          this.overrideAuth = true

          if (this.user?.import_progress) {
            this.user.import_progress = JSON.parse(this.user.import_progress)
          }

          sessionStorage.setItem('cdp_auth_override_token', overrideAuthToken)
        } catch (e) {
          handleError(e, { component: 'UserStore', action: 'checkSessionOverrideAuth' })
          this.user = null
          this.loggedIn = false
        }
      }

      if (!this.loggedIn || !this.user) {
        let authData = localStorage.getItem("cdp_auth")
        if (authData !== null && authData !== "") {
          try {
            const data = JSON.parse(authData)
            this.token = data.token
            this.user = data.user
            this.loggedIn = true
          } catch (e) {
            handleError(e, { component: 'UserStore', action: 'checkSessionParseAuth' })
            this.loggedIn = false
          }
        }

        if (this.loggedIn) {
          apiClient().get(`/users/me`).then(response => {
            this.user = response.data
            if (this.user?.import_progress) {
              this.user.import_progress = JSON.parse(this.user.import_progress)
            }
            const authData = {
              token: this.token,
              user: response.data,
            }
            localStorage.setItem("cdp_auth", JSON.stringify(authData))
          })
        }
      }
    },

    async logIn(email, password) {
      this.loginError = null
      let success = false

      try {
        const response = await apiClientWithoutAuth().post('/users/login/', {email, password})
        
        const data = response.data

        if (data?.ok) {
          const sessionData = {
            token: data.token,
            user: data.user,
          }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))

          this.token = data.token
          this.user = data.user
          // this.email = data.user.email
          this.loggedIn = true
          success = true
        } else {
          this.loginError = 'Login failed'
        }
      } catch (e) {
        handleApiError(e, { component: 'UserStore', action: 'logIn' })
        this.loginError = e.response?.data?.message
      }
      return success
    },

    async logOut() {
      const globalStore = useGlobalStore()
      const configStore = useConfigStore()

      window.Cello && window.Cello("shutdown")
      globalStore.celloAlreadyBooted = false
      localStorage.removeItem('cdp_auth')
      localStorage.removeItem("collx_user")
      sessionStorage.removeItem('cdp_auth_override_token')
      this.token = ""
      this.loggedIn = false
      configStore.config = {}
    },

    async createUser(body) {
      try {
        const data = await this.lookupEmail(body.email)
        if (data?.exists && data?.verified) {
          this.emailAlreadyExists = true
          return
        }

        if (data?.exists && !data?.verified) {
          this.userCreated = true

          await this.reverificationEmail(body.email)
          return
        }

        await apiClient().post('/users', body)
        this.emailAlreadyExists = false
        this.userCreated = true
      } catch (error) {
        if (error.response?.status === 406 || error.response?.data?.message?.includes('already exists')) {
          this.emailAlreadyExists = true
        } else {
          handleApiError(error, { component: 'UserStore', action: 'createUser' })
        }
      }
    },

    async verificationResetPassword(token) {
      this.verificationLinkExpired = false

      try {
        const { data, response } = await apiClient().get(`/users/verify-reset-password/${token}`)

        if (data?.ok) {
          this.token = token

          this.verificationFail = false
          this.verificationSuccess = true
        } else {
          this.verificationFail = true

          this.verificationLinkExpired = true
        } 
      } catch (error) {
        this.verificationFail = true
        handleApiError(error, { component: 'UserStore', action: 'verificationResetPassword' })

        this.verificationLinkExpired = true
      }
    },

    async updatePassword(body) {
      try {
        const { data } = await apiClientWithoutAuth().post(`/users/update-password`, { ...body, token: this.token })

        if (data?.ok) {
          this.token = data.token

          const { data: userData } = await await apiClient().get(`/users/me`)

          if (userData.import_progress) {
            userData.import_progress = JSON.parse(userData.import_progress)
          }

          const sessionData = {
            token: data.token,
            user: userData,
          }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))

          this.user = userData
          this.loggedIn = true

          this.updatePasswordFail = false
          this.updatePasswordSuccess = true
        } else {
          this.updatePasswordFail = true
          throw(new Error('updatePassword'))
        }
      } catch (error) {
        this.updatePasswordFail = true
        handleApiError(error, { component: 'UserStore', action: 'updatePassword' })
      }
    },

    async verificationEmail(token) {
      this.verificationLinkExpired = false

      try {
        const { data } = await apiClient().get(`/users/verify-email/${token}`)

        if (data?.ok) {
          localStorage.removeItem("cdp_auth")
          this.token = ""
          this.loggedIn = false

          const sessionData = {
            token: data.token,
            user: data.user,
          }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))

          this.token = data.token
          this.user = data.user
          
          this.loggedIn = true

          this.verificationFail = false
          this.verificationSuccess = true

         
        } else {
          this.verificationFail = true

          this.verificationLinkExpired = true
        } 
      } catch (error) {
        this.verificationFail = true
        handleApiError(error, { component: 'UserStore', action: 'verificationEmail' })

        this.verificationLinkExpired = true
      }
    },

    async reverificationEmail(email) {
      try {
        const { data } = await apiClient().post(`/users/email-verification`, { email })

        if (data?.ok) {
          this.verificationFail = false
          this.verificationSuccess = true
        } else {
          this.verificationFail = true
        } 
      } catch (error) {
        this.verificationFail = true
        handleApiError(error, { component: 'UserStore', action: 'reverificationEmail' })
      }
    },

    async lookupEmail(email) {
      try {
        const { data } = await apiClient().post(`/users/lookup`, {
          email
        })
       
        this.lookupEmailInfo = { ...data }

        return data
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'lookupEmail' })
      }
    },

    async resetPassword(body) {
      try {
        this.notFoundEmail = false

        await apiClient().post(`/users/reset-password`, body)
      } catch (error) {
        if (error.response.data.message === 'The email does not exist') {
          this.notFoundEmail = true
        }
        handleApiError(error, { component: 'UserStore', action: 'resetPassword' })
      }
    },

    async fetchScanners() {
      try {
        const { data } = await apiStrapi(
          '/scanner-models', 
          {
              populate: '*',
          }
        )

        const mappedScanners = data.map(scanner => {
          return scanner?.attributes?.section
        })

        this.scanners = mappedScanners
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'resetPassword' })
      }
    },

    async fetchUser() {
      try {
        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        const { data } = await apiClient().get('/users/me')

        this.user = { ...data }

        if (this.user?.import_progress) {
          this.user.import_progress = JSON.parse(this.user.import_progress)
        }

        if (sessionData) {
          sessionData.user = { ...data }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
        }

        const skuFields = ['auto_generate_sku', 'sku_prefix', 'sku_starting_number', 'sku_type', 'sku_width', 'use_cert_as_sku']
        skuFields.forEach(field => {
          if ((this.settings[field] === undefined || this.settings[field] === null) && this.user[field] != null) {
            this.settings[field] = this.user[field]
          }
        })
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'fetchUser' })
      }
    },

    async updateUser(body) {
      try {
        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData) || {}

        const { data } = await apiClient().patch('/users/me', body)
        this.user = { ...this.user, ...data, banner_placement: sessionData.user?.banner_placement }

        sessionData.user = { ...this.user }
        localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'updateUser' })
      }
    },

    async deleteUser(body) {
      try {
        const { data } = await apiClient().delete('/users/me', body)
        this.logOut()
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'deleteUser' })
      }
    },

    async updateAvatar(avatar) {
      try {
        this.uploadingAvatar = true
        const { data } = await apiClient().post('/users/me/avatar-image', avatar)

        this.user = { ...this.user, ...data  }

        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        sessionData.user = { ...this.user }
        localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
        this.uploadingAvatar = false
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'updateAvatar' })
        this.uploadingAvatar = false
      }
    },

    async removeAvatar() {
      try {
        const { data } = await apiClient().delete('/users/me/avatar-image')

        this.user = { ...this.user, avatar_image: '' }

        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        sessionData.user = { ...this.user }
        localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'removeAvatar' })
      }
    },

    async addBanner(banner, placement) {
      try {
        this.uploadingBanner = true
        const { data } = await apiClient().post('/users/me/banner-image', banner)

        // Response is `{ status, banner_image, banner_placement }` — take the
        // two fields rather than spreading the envelope, which would put
        // `status: true` on the user object.
        //
        // Placement comes from the RESPONSE now, not the argument: it's what the
        // server actually stored, and `batchStore.addBannerToBatch` reads it
        // straight after this to build its `location` param.
        this.user = {
          ...this.user,
          banner_image: data?.banner_image ?? this.user.banner_image,
          banner_placement: data?.banner_placement ?? placement,
        }

        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        sessionData.user = { ...this.user }
        localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
        this.uploadingBanner = false
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'addBanner' })
        this.uploadingBanner = false
      }
    },

    async removeBanner() {
      try {
        const { data } = await apiClient().delete('/users/me/banner-image')

        this.user = { ...this.user, banner_image: ''  }

        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        sessionData.user = { ...this.user }
        localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'removeBanner' })
      }
    },

    async fetchCustomFields() {
      try {
        const { data } = await apiClient().get('/custom-fields')

        this.customFields = data.map(item => ({...item, editing: false}))
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'fetchCustomFields' })
      }
    },

    async createCustomField(body) {
      try {
        const { data } = await apiClient().post('/custom-fields', body)

        this.fetchCustomFields()
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'createCustomField' })
      }
    },

    async updateCustomField(field) {
      try {
        const { data } = await apiClient().patch(`/custom-fields/${field.id}`, field)

        this.fetchCustomFields()
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'updateCustomField' })
      }
    },

    async deleteCustomField(fieldId) {
      try {
        const { data } = await apiClient().delete(`/custom-fields/${fieldId}`)

        this.fetchCustomFields()
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'deleteCustomField' })
      }
    },

    async changePassword(body) {
      try {
        const { data, response } = await apiClient().post('/users/me/change-password', body)

        if (response) {
          this.changePasswordError = response.data
        }
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'changePassword' })
      }
    },

    async fetchTemplates() {
      try {
        this.loadingTemplates = true
        const { data } = await apiClient().get('/templates')

        const titleTemplates = []
        const descriptionTemplates = []

        for(const template of data) {
          template.type === 'title' && titleTemplates.push(template)
          template.type === 'description' && descriptionTemplates.push(template)
        }

        this.titleTemplates = titleTemplates
        this.descriptionTemplates = descriptionTemplates
        this.loadingTemplates = false
      } catch (error) {
        this.loadingTemplates = false
        handleApiError(error, { component: 'UserStore', action: 'fetchTemplates' })
      }
    },

    async createTemplate(body, type) {
      const configStore = useConfigStore()
      try {
        const { data } = await apiClient().post('/templates', body)

        this[type] = [ ...this[type], data]

        configStore.fetchConfigs()

        return data
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'createTemplate' })
      }
    },

    async updateTemplate(id, body, key) {
      const configStore = useConfigStore()

      try {
        const { data } = await apiClient().put(`/templates/${id}`, body)

        this[key] = this[key].reduce((acc, item) => {
          if (item.id !== id) {
            acc.push(item)
          } else {
            acc.push(body)
          }

          return acc
        }, [])

        configStore.fetchConfigs()

        return data
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'updateTemplate' })
      }
    },

    async deleteTemplate(id, type) {
      const configStore = useConfigStore()

      try {
        const { data } = await apiClient().delete(`/templates/${id}`)

        this[type] = this[type].reduce((acc, item) => {
          if (item.id !== id) {
            acc.push(item)
          }

          return acc
        }, [])

        configStore.fetchConfigs()
        
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'deleteTemplate' })
      }
    },

    async validatePromoCode(code) {
      try {
        const { data } = await apiClient().post(`/promo_codes/validate`, {
          code
        })

        if (data?.ok) {
          this.promoCodeInfo = { ...data.promo_code, message: data.message }

          this.promoCodeApplied = true

          return this.promoCodeInfo
        } else {
          this.promoCodeError = data.message
          this.promoCodeApplied = false
        }
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'validatePromoCode' })
        this.promoCodeError = error.response?.data?.message
        this.promoCodeApplied = false
      }
    },

    async captureWinbackCampaign(body) {
      try {
        const { data, response } = await apiClient().post(`/users/capture-campaign`, body)

        if (!data && !response?.data?.ok) {
          this.lookupWinbackInfo = null
        }

        if (data?.ok) {
          this.lookupWinbackInfo = data.user
        }

      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'captureWinbackCampaign' })
        handleError(error, {
          category: 'api',
          action: 'capture-winback-campaign',
          component: 'UserStore',
        })
      }
    },

    async linkToMarketplace(code, marketplaceName) {
      try {
        this.linkToMarketplaceLoader = true

        const { data } = await apiClient().post(`/users/me/link-${marketplaceName}`, { code })

        if (data?.ok) {
          await this.fetchUser()
          this.linkToMarketplaceLoader = false
        }
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'linkToMarketplace' })
        handleError(error, {
          category: 'api',
          action: 'link-to-marketplace',
          component: 'UserStore',
          data: { marketplaceName },
        })
        this.linkToMarketplaceLoader = false
      }
    },

    async unlinkToChannel(type) {
      try {
        const { data } = await apiClient().post(`/users/me/unlink-${type}`)
        if (data?.ok || (!data?.connected && data?.message.includes('Shopify account disconnected successfully'))) {
          await this.fetchUser()
        }
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'unlinkToChannel' })
        handleError(error, {
          category: 'api',
          action: 'unlink-to-channel',
          component: 'UserStore',
          data: { type },
        })
      }
    },

    async loginOtpPassword(password) {
      try {
        this.loginOtpPasswordLoader = true
        const { data } = await apiClient().post(`users/otp-login`, {
          password
        })

        if (data?.ok) {
          const sessionData = {
            token: data.token,
            user: data.user,
          }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))

          this.token = data.token
          this.user = data.user
          this.loggedIn = true
          this.loginOtpPasswordLoader = false
          this.redirectMobileScanUrl = data.redirect_to

          return  data.redirect_to
        }

        if (data?.message) {
          this.loginOtpPasswordErrorMessage = data.message
        }

        this.loginOtpPasswordLoader = false
      } catch (error) {
        this.loginOtpPasswordLoader = false

        this.loginOtpPasswordErrorMessage = error.response?.data?.message
        handleApiError(error, { component: 'UserStore', action: 'loginOtpPassword' })
      }
    },

    async sendEvent(event, payload = {}) {
      try {
        const { data } = await apiClient().post(`users/me/events`, {
          event,
          payload
        })
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'sendEvent' })
      }
    },

    async getUCCCodeAndUpdateUser() {
      if (this.user?.cello_referrer_code) return

      try {
        window.CelloAttribution('getUcc').then(async (result) => {
          const ucc = result
          await apiClient().patch('/users/me', { cello_referrer_code: ucc })
          window.CelloAttribution('deleteCookies')
        })
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'getUCCCodeAndUpdateUser' })
      }
    },

    async fetchUserTaxInfo(location = {}) {
      try {
        const { data } = await apiClient().post('/users/me/taxes', { price: this.selectedPlanPrice, ...location })

        this.tax = data
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'fetchUserTaxInfo' })
      }
    },

    /**
     * Address verification — US only, and fail-open.
     *
     * The backend verifies through Avalara, whose coverage is US addresses.
     * Anything else came back `valid: false`, and since every save is gated on
     * this flag, users outside the US simply could not save an address: the
     * form kept reverting to their last saved country (usually United States).
     * So we only ask about US addresses and accept the rest as entered — we
     * have nothing to check them against.
     *
     * The `catch` returns TRUE for the same reason. A failed request means we
     * don't know whether the address is good, and "unknown" must not read as
     * "invalid" — an Avalara outage or a dropped connection would otherwise
     * lock everyone out of their own address form. The error still reaches
     * Sentry via `handleApiError`.
     */
    async validationAddress(location) {
      if (location?.country !== 'US') {
        this.validAddress = true

        return true
      }

      try {
        const { data } = await apiClient().post('/users/me/validate-address', { ...location })

        this.validAddress = !!data.valid

        return this.validAddress
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'validationAddress' })

        this.validAddress = true

        return true
      }
    },

    async updateUserDailySaleRecapTime(time) {
      try {
        const { data } = await apiClient().post('/users/me/notifications/daily-pick-hour', {
          enabled: true,
          hour: time,
        })
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'updateUserDailySaleRecapTime' })
      }
    },

    async fetchSettings() {
      try {
        this.loadingSettings = true

        const { data } = await apiClient().get('/users/me/settings')

        const skuFields = ['auto_generate_sku', 'sku_prefix', 'sku_starting_number', 'sku_type', 'sku_width', 'use_cert_as_sku']
        const settings = data?.data ?? {}

        skuFields.forEach(field => {
          if (settings[field] === undefined || settings[field] === null) {
            settings[field] = this.user?.[field] ?? null
          }
        })

        this.settings = settings
        this.loadingSettings = false

        return settings
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'fetchSettings' })
        this.loadingSettings = false
      }
    },

    async updateSettings(body) {
      try {
        const { data } = await apiClient().patch('/users/me/settings', body)

        this.settings = { ...this.settings, ...data?.data }

        return data?.data
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'updateSettings' })
      }
    },

    async fetchConnectionCredentials() {
      try {
        this.loadingConnectionCredentials = true
        const { data } = await apiClient().get('/users/me/connection-credentials')
       
        this.connectionCredentials = data?.data ?? []

        this.loadingConnectionCredentials = false
        return data?.data
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'fetchConnectionCredentials' })
        this.loadingConnectionCredentials = false
      }
    },

    async fetchConnectionCredentialByType(type) {
      try {
        const { data } = await apiClient().get(`/users/me/connection-credentials/${type}`)

        this.connectionCredentials = (this.connectionCredentials ?? []).map(c =>
          c.type === type ? { ...c, ...data?.data } : c
        )
        return data?.data
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'fetchConnectionCredentialByType' })
      }
    },

    async updateConnectionCredential(type, body) {
      try {
        const { data } = await apiClient().put(`/users/me/connection-credentials/${type}`, body)
        this.connectionCredentials = (this.connectionCredentials ?? []).map(c =>
          c.type === type ? { ...c, ...data?.data } : c
        )

        if(Object.keys(body).includes('enabled')) {
          this.user[`${type}_enabled`] = body.enabled
          this.updateUser({ ...this.user, [`${type}_enabled`]: body.enabled })
        }

        return data?.data
      } catch (error) {
        handleApiError(error, { component: 'UserStore', action: 'updateConnectionCredential' })
      }
    },
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
